import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

type AdminSidebarProps = {
  collapsed: boolean;
};

type SidebarItem = {
  label: string;
  to?: string;
  icon: string;
  children?: SidebarItem[];
};

const sidebarItems: SidebarItem[] = [
  {
    label: "Dashboard",
    to: "/admin",
    icon: "◫",
  },
  {
    label: "Envíos",
    icon: "⬚",
    children: [
      {
        label: "Lista de envíos",
        to: "/admin/envios",
        icon: "≣",
      },
      {
        label: "Carga de envíos",
        to: "/admin/envios/carga",
        icon: "▣",
      },
      {
        label: "Impresión de etiquetas",
        to: "/admin/envios/etiquetas",
        icon: "▤",
      },
      {
        label: "Rutear envíos",
        to: "/admin/envios/rutear",
        icon: "↝",
      },
    ],
  },
  {
    label: "Liquidaciones",
    icon: "◭",
    children: [
      {
        label: "Carga manual",
        to: "/admin/liquidaciones/carga-manual",
        icon: "▣",
      },
      {
        label: "Totales",
        to: "/admin/liquidaciones/totales",
        icon: "◩",
      },
    ],
  },
  {
    label: "Tiendas",
    to: "/admin/stores",
    icon: "◎",
  },
  {
    label: "Usuarios",
    to: "/admin/users",
    icon: "◌",
  },
  {
    label: "Sistema",
    icon: "▤",
    children: [
      {
        label: "Listas de precios",
        to: "/admin/sistema/listas-de-precios",
        icon: "▥",
      },
    ],
  },
];

const sidebarStyle = (collapsed: boolean): React.CSSProperties => ({
  width: collapsed ? "92px" : "272px",
  minWidth: collapsed ? "92px" : "272px",
  height: "100vh",
  position: "sticky",
  top: 0,
  borderRight: "1px solid var(--border)",
  background: "var(--surface)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  boxShadow: "var(--shadow)",
  display: "flex",
  flexDirection: "column",
  transition: "width var(--transition-base), min-width var(--transition-base)",
  zIndex: 30,
  overflow: "hidden",
});

const topSectionStyle: React.CSSProperties = {
  padding: "18px 16px 10px 16px",
  flexShrink: 0,
};

const brandRowStyle = (collapsed: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: "14px",
  minHeight: "52px",
});

const logoBoxStyle: React.CSSProperties = {
  width: "44px",
  height: "44px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--primary-soft)",
  border: "1px solid var(--border)",
  color: "var(--primary)",
  fontWeight: 800,
  fontSize: "17px",
  letterSpacing: "0.06em",
  flexShrink: 0,
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const navScrollStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  paddingBottom: "10px",
};

const navStyle: React.CSSProperties = {
  padding: "8px 10px 0 10px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const bottomSectionStyle: React.CSSProperties = {
  padding: "12px 10px 16px 10px",
  flexShrink: 0,
};

const footerCardStyle = (collapsed: boolean): React.CSSProperties => ({
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: collapsed ? "10px" : "12px",
  textAlign: collapsed ? "center" : "left",
});

const getItemBaseStyle = ({
  active,
  collapsed,
}: {
  active: boolean;
  collapsed: boolean;
}): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "space-between",
  gap: "10px",
  minHeight: "46px",
  padding: collapsed ? "0" : "0 14px",
  borderRadius: "18px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.10)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
});

const iconWrapStyle: React.CSSProperties = {
  width: "20px",
  textAlign: "center",
  fontSize: "15px",
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const leftRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const childListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  paddingTop: "5px",
  paddingLeft: "10px",
};

const getChildStyle = (active: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: "9px",
  minHeight: "36px",
  borderRadius: "14px",
  padding: "0 12px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.08)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontSize: "13px",
  fontWeight: active ? 700 : 500,
});

const isGroupActive = (item: SidebarItem, currentPath: string): boolean => {
  if (!item.children?.length) return false;
  return item.children.some((child) =>
    child.to ? currentPath.startsWith(child.to) : false
  );
};

const AdminSidebar = ({ collapsed }: AdminSidebarProps) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const visibleItems = useMemo(() => sidebarItems, []);
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <aside style={sidebarStyle(collapsed)}>
      <div style={topSectionStyle}>
        <div style={brandRowStyle(collapsed)}>
          <div style={logoBoxStyle}>LX</div>

          {!collapsed && (
            <div style={brandMetaStyle}>
              <span style={brandEyebrowStyle}>Plataforma logística</span>
              <span style={brandNameStyle}>Lynx</span>
            </div>
          )}
        </div>
      </div>

      <div style={navScrollStyle}>
        <nav style={navStyle}>
          {visibleItems.map((item) => {
            const hasChildren = Boolean(item.children?.length);
            const isOpen = openGroups[item.label];
            const groupActive = isGroupActive(item, currentPath);

            if (!hasChildren) {
              return (
                <NavLink
                  key={item.to}
                  to={item.to!}
                  end={item.to === "/admin"}
                  style={({ isActive }) =>
                    getItemBaseStyle({
                      active: isActive,
                      collapsed,
                    })
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <div style={leftRowStyle}>
                    <span style={iconWrapStyle}>{item.icon}</span>
                    {!collapsed && <span style={labelStyle}>{item.label}</span>}
                  </div>
                </NavLink>
              );
            }

            return (
              <div key={item.label}>
                <button
                  type="button"
                  onClick={() => !collapsed && toggleGroup(item.label)}
                  style={{
                    ...getItemBaseStyle({
                      active: groupActive,
                      collapsed,
                    }),
                    width: "100%",
                    background: groupActive
                      ? "rgba(198,165,106,0.10)"
                      : "transparent",
                    cursor: "pointer",
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  <div style={leftRowStyle}>
                    <span style={iconWrapStyle}>{item.icon}</span>
                    {!collapsed && <span style={labelStyle}>{item.label}</span>}
                  </div>

                  {!collapsed && (
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "12px",
                      }}
                    >
                      {isOpen ? "▾" : "▸"}
                    </span>
                  )}
                </button>

                {!collapsed && isOpen && (
                  <div style={childListStyle}>
                    {item.children!.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to!}
                        style={({ isActive }) => getChildStyle(isActive)}
                      >
                        <span style={iconWrapStyle}>{child.icon}</span>
                        <span style={labelStyle}>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div style={bottomSectionStyle}>
        <div style={footerCardStyle(collapsed)}>
          {!collapsed ? (
            <>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                  marginBottom: "6px",
                }}
              >
                Entorno
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Panel administrativo
              </div>
            </>
          ) : (
            <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
              ●
            </span>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;